"use client"

import { useState, useEffect } from "react"
import { Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { copyToClipboard } from '@/utils/clipboard'

interface LinkSettingsDialogProps {
  open: boolean
  onClose: () => void
  shareLink: string
  shareCode: string
  description?: string       // 备注信息
  encrypted?: boolean        // 是否加密传输
  allowDownload?: boolean    // 允许下载
  allowClone?: boolean       // 允许克隆
  allowTransfer?: boolean    // 允许转存
  burnAfterRead?: boolean    // 下载后销毁
  maxDownloads?: number      // 最大下载次数
  expireAt?: string          // 过期时间
}

export function LinkSettingsDialog({ 
  open, 
  onClose, 
  shareLink, 
  shareCode,
  description: initialDescription = "",
  encrypted: initialEncrypted = false,
  allowDownload: initialAllowDownload = false,
  allowClone: initialAllowClone = false,
  allowTransfer: initialAllowTransfer = false,
  burnAfterRead: initialBurnAfterRead = false,
  maxDownloads: initialMaxDownloads = 0,
  expireAt = "",
}: LinkSettingsDialogProps) {
  const [copiedLink, setCopiedLink] = useState(false)
  const [copiedCode, setCopiedCode] = useState(false)
  const [encryptEnabled, setEncryptEnabled] = useState(initialEncrypted)
  const [allowOthers, setAllowOthers] = useState(initialAllowDownload)
  const [validity, setValidity] = useState("1")
  const [description, setDescription] = useState(initialDescription)
  
  // 当 props 变化时更新状态
  useEffect(() => {
    setEncryptEnabled(initialEncrypted)
    setAllowOthers(initialAllowDownload)
    setDescription(initialDescription)
  }, [initialEncrypted, initialAllowDownload, initialDescription])

  const handleCopyLink = async () => {
    const success = await copyToClipboard(shareLink)
    if (success) {
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    }
  }

  const handleCopyCode = async () => {
    const success = await copyToClipboard(shareCode)
    if (success) {
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 2000)
    }
  }

  const handleCopyAll = async () => {
    const success = await copyToClipboard(`${shareLink}\n口令: ${shareCode}`)
    if (success) {
      setCopiedLink(true)
      setCopiedCode(true)
      setTimeout(() => {
        setCopiedLink(false)
        setCopiedCode(false)
      }, 2000)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-2xl p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold text-gray-900">链接设置</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 p-6">
          {/* 左侧：链接分享 */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">链接分享</h3>
              <div className="relative">
                <Input 
                  value={shareLink} 
                  readOnly 
                  className="pr-20 bg-gray-50 border-gray-200"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8"
                  onClick={handleCopyLink}
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-3 h-3 mr-1 text-green-600" />
                      <span className="text-green-600 text-xs">已复制</span>
                    </>
                  ) : (
                    <span className="text-xs">复制链接</span>
                  )}
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">口令分享</h3>
              <div className="relative">
                <Input 
                  value={shareCode} 
                  readOnly 
                  className="pr-16 bg-gray-50 border-gray-200"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8"
                  onClick={handleCopyCode}
                >
                  {copiedCode ? (
                    <>
                      <Check className="w-3 h-3 mr-1 text-green-600" />
                      <span className="text-green-600 text-xs">已复制</span>
                    </>
                  ) : (
                    <span className="text-xs">复制</span>
                  )}
                </Button>
              </div>
            </div>

            <Button 
              className="w-full h-11 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-lg font-medium"
              onClick={handleCopyAll}
            >
              一键复制
            </Button>
          </div>

          {/* 右侧：设置 */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">设置</h3>

            {/* 加密传输 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-900">加密传输</span>
                <span className="text-[10px] bg-black text-white px-1.5 py-0.5 rounded">PRO</span>
              </div>
              <Switch 
                checked={encryptEnabled}
                onCheckedChange={setEncryptEnabled}
              />
            </div>

            {/* 允许他人 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-900">允许他人</span>
                <span className="text-[10px] bg-black text-white px-1.5 py-0.5 rounded">PRO</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">下载、转存及克隆</span>
                <Switch 
                  checked={allowOthers}
                  onCheckedChange={setAllowOthers}
                />
              </div>
            </div>

            {/* 有效期 */}
            <div className="space-y-2">
              <label className="text-sm text-gray-900 font-medium">有效期</label>
              <Select value={validity} onValueChange={setValidity}>
                <SelectTrigger className="w-full bg-white border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 天</SelectItem>
                  <SelectItem value="3">3 天</SelectItem>
                  <SelectItem value="7">7 天</SelectItem>
                  <SelectItem value="30">30 天</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 补充描述 */}
            <div className="space-y-2">
              <label className="text-sm text-gray-900 font-medium">补充描述</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="输入你的信息，它将被示收取的用户。"
                className="min-h-[80px] resize-none bg-white border-gray-200"
                readOnly
              />
            </div>
            
            {/* 显示设置信息 */}
            {(initialBurnAfterRead || initialMaxDownloads > 0 || expireAt) && (
              <div className="space-y-2 pt-2 border-t">
                <label className="text-sm text-gray-900 font-medium">下载设置</label>
                <div className="space-y-2 text-sm text-gray-600">
                  {initialBurnAfterRead && (
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-600">⚠️</span>
                      <span>下载后自动销毁</span>
                    </div>
                  )}
                  {initialMaxDownloads > 0 && (
                    <div>
                      <span>最大下载次数：</span>
                      <span className="font-medium">{initialMaxDownloads} 次</span>
                    </div>
                  )}
                  {expireAt && (
                    <div>
                      <span>过期时间：</span>
                      <span className="font-medium">{new Date(expireAt).toLocaleString('zh-CN')}</span>
                    </div>
                  )}
                  {initialEncrypted && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">🔒</span>
                      <span>加密传输</span>
                    </div>
                  )}
                  {(initialAllowDownload || initialAllowClone || initialAllowTransfer) && (
                    <div>
                      <span>允许操作：</span>
                      {initialAllowDownload && <span className="ml-1 text-green-600">下载</span>}
                      {initialAllowClone && <span className="ml-1 text-green-600">克隆</span>}
                      {initialAllowTransfer && <span className="ml-1 text-green-600">转存</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 底部协议 */}
        <div className="px-6 py-4 bg-gray-50 border-t text-center">
          <p className="text-xs text-gray-500">
            拥有者需对链接内容遵守平台规则负责，具体说明请查看{" "}
            <a href="#" className="text-orange-500 hover:underline">使用协议</a>
            {" "}和{" "}
            <a href="#" className="text-orange-500 hover:underline">隐私政策</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

